/**
 * Cliente HTTP del editor de campañas — Fase 8c + edit mode.
 *
 *  - `POST  /api/campaigns`          crear (multipart con cover opcional)
 *  - `GET   /api/campaigns/:id`      cargar para editar
 *  - `PATCH /api/campaigns/:id`      actualizar (multipart; `removeCover=true` quita la portada)
 *  - `DELETE /api/campaigns/:id`     borrar (acción desde el sidebar de la pizarra)
 *
 * El backend exige rol officer+ para POST y autor/admin+ para PATCH/DELETE.
 */

import type { Campaign } from '@51esc/shared';

export type CreateCampaignResult =
  | { ok: true; campaign: Campaign }
  | { ok: false; message: string };

export async function createCampaign(
  fd: FormData,
): Promise<CreateCampaignResult> {
  return submitFormData('/api/campaigns', 'POST', fd);
}

export async function updateCampaign(
  id: string,
  fd: FormData,
): Promise<CreateCampaignResult> {
  return submitFormData(
    `/api/campaigns/${encodeURIComponent(id)}`,
    'PATCH',
    fd,
  );
}

async function submitFormData(
  url: string,
  method: 'POST' | 'PATCH',
  fd: FormData,
): Promise<CreateCampaignResult> {
  let res: Response;
  try {
    res = await fetch(url, { method, credentials: 'include', body: fd });
  } catch (err) {
    console.error('[campania-editor] fetch network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* lo manejamos abajo */
  }
  if (!res.ok) {
    const msg =
      typeof body === 'object' &&
      body !== null &&
      typeof (body as { error?: unknown }).error === 'string'
        ? ((body as { error: string }).error)
        : `El servidor respondió con ${String(res.status)}.`;
    return { ok: false, message: msg };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { campaign?: unknown }).campaign !== 'object' ||
    (body as { campaign?: unknown }).campaign === null
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, campaign: (body as { campaign: Campaign }).campaign };
}

export type FetchCampaignResult =
  | { ok: true; campaign: Campaign }
  | { ok: false; message: string };

export async function fetchCampaign(id: string): Promise<FetchCampaignResult> {
  let res: Response;
  try {
    res = await fetch(`/api/campaigns/${encodeURIComponent(id)}`, {
      credentials: 'include',
    });
  } catch (err) {
    console.error('[campania-editor] fetchCampaign network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    return {
      ok: false,
      message: `El servidor respondió con ${String(res.status)}.`,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { campaign?: unknown }).campaign !== 'object' ||
    (body as { campaign?: unknown }).campaign === null
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, campaign: (body as { campaign: Campaign }).campaign };
}

export async function deleteCampaign(
  id: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/campaigns/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[campania-editor] deleteCampaign network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    let msg = `El servidor respondió con ${String(res.status)}.`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string') msg = body.error;
    } catch {
      /* ignore */
    }
    return { ok: false, message: msg };
  }
  return { ok: true };
}
